import { State } from 'src/state';
import StateObserver from 'src/StateObserver';
import { createPersistentEmitter, Emitter, Listener } from 'src/lib/Emitter';
import { createWaiter } from './utils';

export type Selector<TSubState> = (state: State, now: number) => TSubState;

export class IntervalEmitter<TSubState> {
  private lastState: TSubState | undefined;
  private emitter: Emitter<TSubState>;

  private listeners = 0;
  private waiter = createWaiter();

  constructor(
    private selector: Selector<TSubState>,
    private interval: number,
  ) {
    let lastTime = 0;
    this.emitter = createPersistentEmitter((state) => {
      const now = StateObserver.now();
      if (this.lastState === undefined || Math.abs(now - lastTime) > interval) {
        this.lastState = this.selector(state, now);
        lastTime = now;
      }
      return this.lastState;
    });
  }

  public addListener(
    listener: Listener<TSubState>,
    opts: { noInit?: boolean } = {},
  ) {
    this.emitter.addListener(listener, opts);

    ++this.listeners;
    if (this.listeners === 1) {
      this.start();
    }
  }

  public removeListener(listener: Listener<TSubState>) {
    this.emitter.removeListener(listener);

    --this.listeners;
    if (this.listeners === 0) {
      this.stop();
    }
  }

  public removeAllListeners() {
    this.emitter.detach();
    this.listeners = 0;
    this.stop();
  }

  public start = () => {
    this.emitter.force();
    this.waiter.wait(this.start, this.interval);
  };

  public stop = () => {
    this.lastState = undefined;
    this.waiter.clear();
  };

  public force() {
    this.emitter.force();
  }
}

export default function createIntervalEmitter<TSubState>(
  selector: Selector<TSubState>,
  interval = 1000,
) {
  return new IntervalEmitter<TSubState>(selector, interval);
}
